import { useUserInfo, useUserSettings } from "@next-platform/client-common-core";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { resolveRulebookDiscoverSortBy } from "@next-platform/client-common-core/rulebook/picker/rulebookDiscoverHit";
import type { RulebookSelectionSort } from "@next-platform/client-common-core/rulebook/picker/rulebookSelection";
import { FetchStatus } from "@next-platform/client-common-core/user/userSettings/types";
import { useEffect, useMemo, useState } from "react";
import type { ConfigureProps } from "react-instantsearch";
import InstantSearchAdapter, { type TypesenseInstantsearchAdapterOptions } from "typesense-instantsearch-adapter";

import useShouldFilterNsfwCharacters from "@/components/search/hooks/useShouldFilterNsfwCharacters";
import type { SearchAdapter } from "@/components/search/types";
import { createSearchClientConfig } from "@/components/search/utils/createSearchClientConfig";
import {
  DEFAULT_INCLUDE_FIELDS_FOR_SEARCH_CLIENT_RULEBOOKS,
  DEFAULT_QUERY_BY_FOR_SEARCH_CLIENT_RULEBOOKS,
  NSFW_TOGGLE_LOCALSTORAGE_KEY,
  TYPESENSE_TEXT_MATCH_SORT_PREFIX,
} from "@/constants";
import { typedLocalStorageService } from "@/utils/localStorageRe";

import { createRulebookDiscoverFilters } from "./rulebookDiscover";

const HITS_PER_PAGE = 48;

/**
 * The Discover tab's search orchestration: the Typesense adapter (created once the query may fire), the sorted index
 * name, the InstantSearch `Configure` props (page size + app/visibility/NSFW filters) and whether the first query
 * still waits on the user's settings.
 */
export default function useRulebookDiscoverSearch({
  apiKey,
  collectionName,
  sort,
}: {
  apiKey: string;
  collectionName: string;
  sort: RulebookSelectionSort;
}): {
  adapter: SearchAdapter | null;
  indexName: string;
  configureProps: ConfigureProps;
  shouldDelayQuery: boolean;
} {
  const { showNSFW, areUserSettingsLoaded, areUserSettingLoading } = useUserSettings();
  const { isAuthenticated } = useUserInfo();
  const [stateAdapter, setStateAdapter] = useState<SearchAdapter | null>(null);
  // As in the Lorebook picker: wait for a signed-in user's settings so NSFW filtering uses their real choice.
  const shouldDelayQuery = isAuthenticated && !areUserSettingsLoaded && areUserSettingLoading !== FetchStatus.failed;
  const { shouldFilterNsfwCharacters: shouldFilterNsfw } = useShouldFilterNsfwCharacters({
    isNsfwToggled: typedLocalStorageService.getItem(NSFW_TOGGLE_LOCALSTORAGE_KEY) === "true",
    isShowNsfwEnabled: showNSFW,
  });
  // Sort rides on the index name (relevance first, then createdAt), as for characters and Lorebooks.
  const indexName = `${collectionName}/sort/${TYPESENSE_TEXT_MATCH_SORT_PREFIX},${resolveRulebookDiscoverSortBy(sort)}`;

  useEffect(() => {
    if (shouldDelayQuery) {
      return;
    }
    const config = createSearchClientConfig({
      apiKey,
      queryBy: DEFAULT_QUERY_BY_FOR_SEARCH_CLIENT_RULEBOOKS,
      includeFields: DEFAULT_INCLUDE_FIELDS_FOR_SEARCH_CLIENT_RULEBOOKS,
    });
    setStateAdapter(new InstantSearchAdapter(config as TypesenseInstantsearchAdapterOptions) as SearchAdapter);
  }, [apiKey, shouldDelayQuery]);

  const configureProps = useMemo(
    () =>
      ({
        hitsPerPage: HITS_PER_PAGE,
        filters: createRulebookDiscoverFilters({ applicationId: basicAppConfig.applicationId, shouldFilterNsfw }),
      }) as ConfigureProps,
    [shouldFilterNsfw],
  );

  return { adapter: stateAdapter, indexName, configureProps, shouldDelayQuery };
}
